import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import ErrorMessage from '../Common/ErrorMessage';
import resx from '../../utilities/resx';
import { LOGGLY_LEVEL_ERROR, RESX_GLOBAL, MIXPANEL_REGISTER, REGEX_EMAIL } from '../../Constants';
import LogglyLogger from '../../API/Loggly';
import SignUp from '../../API/SignUp2';
import GeneralUtility from '../../utilities/generalUtility';
import { withMessage } from '../Common/Message';
import Input from '../Common/Input';
import Button from '../Common/Button';
import Validation from './SignupFormValidation';

function SignupContainer({ message }) {
    const [returnUrl, setReturnUrl] = useState();
    const [aGuid, setAGuid] = useState();

    const legalHtml = resx(RESX_GLOBAL, 'SIGNUP2_LEGAL_WARNING')
        .replace('{0}', "href='https://corporate.resaas.com/terms/' class='re-signup-legal-link' target='_blank'")
        .replace(
            '{1}',
            "href='https://corporate.resaas.com/privacy-policy/' class='re-signup-legal-link' target='_blank'"
        );

    const [isSubmitting, setIsSubmitting] = useState(false);
    const fields = ['FirstName', 'LastName', 'Email', 'Password'];
    const [formData, setFormData] = useState({});

    const initialValidationMessagesState = {
        FirstName: '',
        LastName: '',
        Email: '',
        Password: ''
    };

    const [validationMessages, setValidationMessages] = useState(initialValidationMessagesState);

    const [validate, setValidate] = useState({
        validate: false,
        fields: []
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        setFormData(prevState => ({ ...prevState, FirstName: searchParams.get('firstName') }));
        setFormData(prevState => ({ ...prevState, LastName: searchParams.get('lastName') }));
        setFormData(prevState => ({ ...prevState, Email: searchParams.get('email') }));
        setReturnUrl(searchParams.get('ReturnUrl'));
        setAGuid(searchParams.get('a'));
    }, []);

    // for live validation
    useEffect(() => {
        if (validate.validate) {
            const newState = Validation(validate.fields, formData);

            if (newState) {
                setValidationMessages(prevState => ({ ...prevState, ...newState }));
            } else {
                setValidate({
                    validate: false,
                    fields: []
                });
            }
        }
    }, [formData]);

    const setSessionRedirect = val => {
        if (window.Modernizr.sessionstorage) {
            window.sessionStorage.setItem('returnUrl', val);
        }
    };

    const handleChange = e => {
        const newState = {};
        newState[e.currentTarget.name] = e.currentTarget.value;
        setFormData(prevState => ({ ...prevState, ...newState }));
    };

    const handleSubmit = e => {
        e.preventDefault();

        const newState = Validation(fields, formData);

        if (!newState.allFieldsValidated) {
            setValidationMessages(prevState => ({ ...prevState, ...newState }));
            setValidate({
                validate: true,
                fields: fields
            });
        } else {
            const updateState = { ...initialValidationMessagesState };
            setValidationMessages(prevState => ({ ...prevState, ...updateState }));

            setIsSubmitting(true);

            const params = {
                Email: formData.Email,
                Password: formData.Password,
                Lastname: formData.LastName,
                Firstname: formData.FirstName
            };

            if (returnUrl) {
                params.ReturnUrl = returnUrl;
            }

            if (aGuid) {
                params.Aguid = aGuid;
            }

            SignUp.add(params)
                .then(response => {
                    const success = _path(['data', 'Domain'], response) && _path(['status'], response) === 200;

                    if (!success) {
                        throw new Error();
                        return;
                    }

                    // prefer set redirect for after onboardwizard
                    if (returnUrl) {
                        setSessionRedirect(returnUrl);
                    }

                    // track in ga then redirect to onboard wizard
                    let isRedirected = false;

                    const redirect = () => {
                        if (!isRedirected) {
                            isRedirected = true;
                            window.location.assign(`/myaccount/verificationpending?email=${params.Email}`);
                        }
                    };

                    if (window.ga) {
                        setTimeout(redirect, 1000);
                        window.ga('send', 'event', 'New Account', 'Register', {
                            hitCallback: redirect
                        });
                    } else {
                        window.location.assign(`/myaccount/verificationpending?email=${params.Email}`);
                    }
                })
                .catch(error => {
                    if (_path(['response', 'status'], error) === 409) {
                        // valid error message
                        const errorMessageFull = _path(['response', 'data'], error);
                        errorMessageFull.split('\n').forEach(err => {
                            if (err.trim()) {
                                message.addMessage({ message: err, type: 'error', perpetuate: false });
                            }
                        });
                    } else {
                        // generic error
                        message.addMessage({
                            message: resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE'),
                            type: 'error'
                        });
                        // loggly
                        LogglyLogger.add(LOGGLY_LEVEL_ERROR, 'SignUp.add from Signup2 Page', error);
                    }
                    if (_path(['RESAAS', 'MixPanel', 'trackEvent'], window)) {
                        window.RESAAS.MixPanel.trackEvent(MIXPANEL_REGISTER, {
                            type: 'failed',
                            firstname: params.Firstname,
                            lastname: params.Lastname,
                            email: params.Email
                        });
                    }
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    };

    return (
        <div className="re-entry-form-wrapper">
            <form className="js-signup-form formV2" onSubmit={handleSubmit} noValidate>
                <fieldset>
                    <legend>{resx(RESX_GLOBAL, 'SIGNUP2_RESAAS_FIELDSET_LEGEND')}</legend>
                    <div className="re-grid-container">
                        <div className="re-grid-row">
                            <div className="re-grid-col-xs-24">
                                <div className="re-header">{resx(RESX_GLOBAL, 'SIGNUP2_LF_HEADER')}</div>
                            </div>
                        </div>

                        <div className="re-grid-row">
                            <div className="re-grid-col-xs-24">
                                <div className="re-sub-header">{resx(RESX_GLOBAL, 'SIGNUP2_LF_SUBHEADER')}</div>
                            </div>
                        </div>

                        <div className="re-grid-row">
                            <div className="re-grid-col-md-12">
                                <Input
                                    title={resx(RESX_GLOBAL, 'SIGNUP2_FIRST_NAME_LOWER')}
                                    type="text"
                                    maxlength={49}
                                    name="FirstName"
                                    id="FirstName"
                                    value={formData.FirstName}
                                    onChange={handleChange}
                                    onBlur={handleChange}
                                    required={true}
                                />
                                <ErrorMessage validationMessages={validationMessages} fieldName="FirstName" />
                            </div>
                            <div className="re-grid-col-md-12">
                                <Input
                                    title={resx(RESX_GLOBAL, 'SIGNUP2_LAST_NAME_LOWER')}
                                    type="text"
                                    maxlength={29}
                                    name="LastName"
                                    id="LastName"
                                    value={formData.LastName}
                                    onChange={handleChange}
                                    onBlur={handleChange}
                                    required={true}
                                />
                                <ErrorMessage validationMessages={validationMessages} fieldName="LastName" />
                            </div>
                        </div>
                        <div className="re-grid-row">
                            <div className="re-grid-col-xs-24">
                                <Input
                                    title={resx(RESX_GLOBAL, 'SIGNUP2_EMAIL_ADDRESS')}
                                    type="email"
                                    name="Email"
                                    id="Email"
                                    value={formData.Email}
                                    onChange={handleChange}
                                    onBlur={handleChange}
                                    pattern={REGEX_EMAIL}
                                    required={true}
                                />
                                <ErrorMessage validationMessages={validationMessages} fieldName="Email" />
                            </div>
                        </div>
                        <div className="re-grid-row">
                            <div className="re-grid-col-xs-24">
                                <Input
                                    title={resx(RESX_GLOBAL, 'SIGNUP2_PASSWORD_LOWER')}
                                    type="password"
                                    name="Password"
                                    value={formData.Password}
                                    placeholder={resx(RESX_GLOBAL, 'SIGNUP2_PASSWORD_HINT')}
                                    onChange={handleChange}
                                    onBlur={handleChange}
                                    minlength={6}
                                    maxlength={50}
                                    required={true}
                                />
                                <ErrorMessage validationMessages={validationMessages} fieldName="Password" />
                            </div>
                        </div>
                        <div className="re-grid-row">
                            <div className="re-grid-col-xs-24">
                                <div className="formV2-actions">
                                    <Button
                                        type="submit"
                                        text={resx(RESX_GLOBAL, 'SIGNUP2_SUBMIT_BUTTON')}
                                        className={`re-btn re-btn-full-width ${isSubmitting ? 'submitting' : ''}`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="re-grid-row">
                            <div className="re-grid-col-xs-24">
                                <div className="re-privacy" dangerouslySetInnerHTML={{ __html: legalHtml }} />
                            </div>
                        </div>
                    </div>
                </fieldset>
            </form>
        </div>
    );
}

SignupContainer.propTypes = {
    message: PropTypes.object
};

export default withMessage(SignupContainer);
